/* Public TikTok reels grid — /tiktoks
 *
 * Purpose: show every FreightPilotAI TikTok as permanent social proof.
 * Visitors scroll through the founder's owner-op stories + walkthroughs,
 * see we ACTUALLY ship content weekly, and click through to sign up.
 *
 * Uses TikTok's official embed script (loaded from
 * https://www.tiktok.com/embed.js). The script auto-decorates any
 * <blockquote class="tiktok-embed"> present in the DOM, so we just
 * paste the oEmbed html we cached server-side.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  VideoCamera, ArrowSquareOut, Sparkle, MagnifyingGlass, ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../lib/api";
import { useSeoHead } from "./landing/utils";

const CATEGORY_LABELS = {
  "scam-alert": { label: "Broker alerts", emoji: "⚠" },
  "how-to": { label: "How to spot scams", emoji: "🎓" },
  "walkthrough": { label: "Product walkthroughs", emoji: "🛠️" },
  "carrier-story": { label: "Carrier stories", emoji: "🚛" },
  "other": { label: "More", emoji: "✨" },
};

/* Load the TikTok embed script once per page — TikTok's script decorates
   any <blockquote class="tiktok-embed"> found in the DOM the first time
   it runs. Subsequent DOM changes (filter switches) need us to
   re-inject or manually re-invoke. We do it with the `?t=` cachebust
   trick so React re-mounting a blockquote triggers a re-parse. */
function useTikTokEmbedScript() {
  useEffect(() => {
    const SRC = "https://www.tiktok.com/embed.js";
    let script = document.querySelector(`script[src^="${SRC}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    // Force TikTok to re-parse any newly-mounted embed blockquotes
    const id = setInterval(() => {
      if (window?.tiktokEmbed?.lifecycle?.mount) {
        window.tiktokEmbed.lifecycle.mount();
      }
    }, 500);
    return () => clearInterval(id);
  }, []);
}


export default function TikTokReelsPage() {
  useTikTokEmbedScript();
  useSeoHead(
    "Owner-Op TikToks — FreightPilotAI",
    "Weekly TikTok exposés of freight brokers on the FreightPilotAI DNP list. Watch, share, and search any MC# free before you take a load.",
  );
  const [state, setState] = useState({ loading: true, posts: [], categories: [], total: 0 });
  const [category, setCategory] = useState("all");

  const load = useCallback(async (cat) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const params = cat && cat !== "all" ? { category: cat } : {};
      const r = await client.get("/tiktoks", { params });
      setState({
        loading: false,
        posts: r.data?.posts || [],
        categories: r.data?.categories || [],
        total: r.data?.total || 0,
      });
    } catch (e) {
      setState({ loading: false, posts: [], categories: [], total: 0 });
      toast.error("Failed to load TikToks");
    }
  }, []);

  useEffect(() => { load(category); }, [load, category]);

  const catButtons = useMemo(() => {
    const total = state.categories.reduce((a, b) => a + b.count, 0);
    return [{ id: "all", count: total, label: "All" }, ...state.categories.map((c) => ({
      id: c.id,
      count: c.count,
      label: CATEGORY_LABELS[c.id]?.label || c.id,
    }))];
  }, [state.categories]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="tiktok-reels-page">
      <ReelsHero total={state.total} />

      {/* Category filter chips */}
      <div className="border-b border-white/10 bg-black/40 sticky top-0 z-30 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex gap-2 overflow-x-auto no-scrollbar">
          {catButtons.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategory(c.id)}
              data-testid={`tiktok-reels-cat-${c.id}`}
              aria-pressed={category === c.id}
              className={`shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold whitespace-nowrap transition-colors ${category === c.id
                ? "bg-[#FF5A1F] text-black"
                : "bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border border-white/10"}`}
            >
              {CATEGORY_LABELS[c.id]?.emoji ? <span aria-hidden>{CATEGORY_LABELS[c.id]?.emoji}</span> : null}
              {c.label}
              <span className={`text-[10px] font-mono ${category === c.id ? "text-black/60" : "text-white/40"}`}>
                {c.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        {state.loading ? (
          <div className="text-white/50 text-sm py-14 text-center">Loading TikToks…</div>
        ) : state.posts.length === 0 ? (
          <EmptyState category={category} />
        ) : (
          <div
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="tiktok-reels-grid"
          >
            {state.posts.map((p) => (
              <ReelCard key={p.id} post={p} />
            ))}
          </div>
        )}
      </div>

      <CtaStrip />
    </div>
  );
}


function ReelsHero({ total }) {
  return (
    <section
      className="relative overflow-hidden border-b border-white/10 bg-gradient-to-b from-[#FF5A1F]/10 via-transparent to-transparent"
      data-testid="tiktok-reels-hero"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
        <div className="max-w-3xl">
          <div className="overline text-[#FF9764] mb-3 uppercase tracking-widest text-xs font-bold">
            🎥 Public receipts · every video we've made
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            The <span className="text-[#FF5A1F]">real owner-op stories</span> we film — on video.
          </h1>
          <p className="text-neutral-300 text-base sm:text-lg leading-relaxed mt-5 max-w-2xl">
            {total > 0 ? (
              <><b className="text-white">{total}</b> TikToks so far. Every video is a real broker MC#, cross-checked against the FreightPilotAI DNP list. Share the ones your truckers should see.</>
            ) : (
              <>New TikToks land here every week. Each one names a real broker MC# from the FreightPilotAI DNP list. Bookmark this page.</>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <a
              href="https://www.tiktok.com/@freightpilotai"
              target="_blank"
              rel="noreferrer"
              data-testid="tiktok-reels-follow"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#FF5A1F] text-black text-sm font-bold px-4 py-2 hover:bg-[#FF7038] transition-colors"
            >
              Follow @freightpilotai on TikTok
              <ArrowSquareOut size={12} weight="bold" />
            </a>
            <Link
              to="/dnp"
              data-testid="tiktok-reels-dnp"
              className="inline-flex items-center gap-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white text-sm font-semibold px-4 py-2 border border-white/10 transition-colors"
            >
              <MagnifyingGlass size={13} weight="bold" />
              Search a broker MC free
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}


function ReelCard({ post }) {
  const wrapRef = useRef(null);
  useEffect(() => {
    // Re-invoke TikTok's mount when this card is first inserted so
    // filter-switches re-parse the newly mounted blockquote.
    if (window?.tiktokEmbed?.lifecycle?.mount) {
      window.tiktokEmbed.lifecycle.mount();
    }
  }, [post.id]);

  const cat = CATEGORY_LABELS[post.category] || CATEGORY_LABELS.other;

  return (
    <article
      ref={wrapRef}
      data-testid={`tiktok-reel-${post.id}`}
      className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:border-white/25 transition-colors flex flex-col"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[9px] uppercase tracking-widest bg-[#FF5A1F]/20 border border-[#FF5A1F]/40 text-[#FF9764] font-bold px-2 py-0.5 rounded">
          {cat.emoji} {cat.label}
        </span>
        {post.featured && (
          <span
            data-testid={`tiktok-reel-${post.id}-featured`}
            className="text-[9px] uppercase tracking-widest bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 font-bold px-2 py-0.5 rounded inline-flex items-center gap-1"
          >
            <Sparkle size={9} weight="fill" /> Featured
          </span>
        )}
      </div>

      {/* TikTok embed — the oEmbed HTML is a <blockquote>+<script>.
          We wrap it in a div and use dangerouslySetInnerHTML because the
          HTML is server-generated by TikTok's oEmbed and only stored
          after admin approval — the founder is the only writer. */}
      <div
        className="rounded-lg overflow-hidden bg-black flex-1 flex items-start justify-center"
        data-testid={`tiktok-reel-${post.id}-embed`}
        // TikTok script auto-decorates the blockquote — we skip
        // sanitization here because the HTML comes from our own admin
        // path (oEmbed cached server-side after auth).
        dangerouslySetInnerHTML={{ __html: post.embed_html || _fallbackEmbed(post) }}
      />

      {post.caption && (
        <div className="text-[13px] text-white/70 mt-3 leading-snug line-clamp-2">
          {post.caption}
        </div>
      )}

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/5">
        <a
          href={post.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-[#FF5A1F] font-bold hover:text-[#FF7038]"
          data-testid={`tiktok-reel-${post.id}-open`}
        >
          Watch on TikTok <ArrowSquareOut size={10} weight="bold" />
        </a>
        <div className="text-[10px] text-white/40 ml-auto font-mono">
          {post.added_at?.slice(0, 10) || ""}
        </div>
      </div>
    </article>
  );
}


function _fallbackEmbed(post) {
  // If the oEmbed HTML is missing (shouldn't happen but safety net),
  // render a click-through card with the thumbnail.
  const thumb = post.thumbnail_url ? `<img src="${post.thumbnail_url}" alt="" style="width:100%;aspect-ratio:9/16;object-fit:cover;">` : "";
  return `<a href="${post.url}" target="_blank" rel="noreferrer" style="display:block;">${thumb}<div style="padding:10px;color:#9ca3af;font-size:12px;">Watch on TikTok →</div></a>`;
}


function EmptyState({ category }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/15 p-14 text-center">
      <VideoCamera size={48} className="mx-auto text-white/25 mb-4" />
      <div className="text-white/70 font-bold mb-1">
        {category === "all" ? "No TikToks yet — check back soon" : "No TikToks in this category yet"}
      </div>
      <div className="text-white/50 text-sm max-w-md mx-auto">
        We're actively filming founder-story videos. Follow{" "}
        <a href="https://www.tiktok.com/@freightpilotai" target="_blank" rel="noreferrer" className="text-[#FF5A1F] hover:underline">@freightpilotai</a>{" "}
        to catch the next drop.
      </div>
    </div>
  );
}


function CtaStrip() {
  return (
    <section className="border-t border-white/10 bg-black/40">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-14 text-center">
        <div className="text-[11px] uppercase tracking-widest text-[#FF9764] font-bold mb-2">Don't get scammed. Check first.</div>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-3">
          Search any freight broker MC# — <span className="text-[#FF5A1F]">free</span>.
        </h2>
        <p className="text-neutral-300 text-sm sm:text-base leading-relaxed max-w-xl mx-auto mb-6">
          The FreightPilotAI Broker Directory has hundreds of broker payment reports cross-referenced against FMCSA data. Two clicks and you know whether to take the load.
        </p>
        <Link
          to="/dnp"
          data-testid="tiktok-reels-final-cta"
          className="inline-flex items-center gap-1.5 rounded-md bg-[#FF5A1F] text-black text-sm font-bold px-5 py-2.5 hover:bg-[#FF7038] transition-colors"
        >
          Open the DNP search
          <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    </section>
  );
}
